/*
This script continuously loads posts as the user scrolls the page. It uses the IntersectionObserver API
to detect when the last post is in view. When the last post is in view, it fetches the next page and appends
the posts to the page. It coordinates with the tag filter module to ensure active filters are respected.
*/

import { matchesTag, getActiveTag } from "./tagUtils";
import initMahjongRenderer from "./mahjong";
import initCodeHighlighting from "./codeHighlight";

let nextLink = typeof document !== "undefined" ? document.querySelector('link[rel="next"]')?.getAttribute('href') : null;
let isFetching = false;
let allPagesLoaded = !nextLink;
let observerInstance = null;

export function hasMorePages() {
    return !allPagesLoaded && Boolean(nextLink);
}

// Fetch and parse next page
async function getNextPage(url) {
    try {
        const res = await fetch(url);
        if (!res.ok) {
            throw new Error(`Failed to fetch page: ${res.status}`);
        }

        const nextPageHtml = await res.text();
        const parser = new DOMParser();
        const parsed = parser.parseFromString(nextPageHtml, 'text/html');

        let posts = Array.from(parsed.querySelectorAll('#gh-postfeed > .gh-card, .gh-postfeed > .gh-card, .gh-postfeed > .post, .gh-postfeed > article'));
        if (posts.length === 0) {
            posts = Array.from(parsed.querySelectorAll('.gh-card, .post'));
        }

        const nextUrl = parsed.querySelector('link[rel="next"]')?.getAttribute('href') || null;
        return { posts, nextUrl };
    } catch (error) {
        console.error('Error fetching next page:', error);
        return { posts: [], nextUrl: null };
    }
}

export async function loadNextBatch(onBatchLoaded) {
    if (isFetching || allPagesLoaded || !nextLink) {
        return [];
    }

    isFetching = true;
    const url = nextLink;
    const feed = document.getElementById("gh-postfeed") || document.querySelector(".gh-postfeed");

    try {
        const { posts, nextUrl } = await getNextPage(url);
        nextLink = nextUrl;
        if (!nextLink) {
            allPagesLoaded = true;
            if (observerInstance) {
                observerInstance.disconnect();
            }
        }

        const currentActiveTag = typeof getActiveTag === "function" ? getActiveTag() : "all";
        const appendedPosts = [];

        if (feed && posts.length > 0) {
            posts.forEach((post) => {
                if (matchesTag(post, currentActiveTag)) {
                    post.style.display = "";
                } else {
                    post.style.display = "none";
                }
                feed.append(post);
                appendedPosts.push(post);
            });

            if (typeof initMahjongRenderer === "function") initMahjongRenderer();
            if (typeof initCodeHighlighting === "function") initCodeHighlighting();
        }

        if (onBatchLoaded && appendedPosts.length > 0) {
            onBatchLoaded(appendedPosts);
        }

        if (observerInstance && nextLink) {
            const lastPost = document.querySelector('.gh-postfeed .post:last-of-type, .gh-postfeed .gh-card:last-of-type');
            if (lastPost) {
                observerInstance.observe(lastPost);
            }
        }

        return appendedPosts;
    } finally {
        isFetching = false;
    }
}

export async function loadAllRemainingPages(onBatchLoaded) {
    while (hasMorePages() && !isFetching) {
        const posts = await loadNextBatch(onBatchLoaded);
        if (posts.length === 0 && !hasMorePages()) {
            break;
        }
    }
}

export default function infiniteScroll() {
    nextLink = document.querySelector('link[rel="next"]')?.getAttribute('href');
    if (!nextLink) {
        allPagesLoaded = true;
        return;
    }

    const options = {
        // When the last card is within 200px of the viewport, fetch the next page.
        rootMargin: '200px',
    };

    const callback = (entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                observer.unobserve(entry.target);
                if (hasMorePages()) {
                    loadNextBatch().then(() => {
                        if (hasMorePages()) {
                            const lastPost = document.querySelector('.gh-postfeed .post:last-of-type, .gh-postfeed .gh-card:last-of-type');
                            if (lastPost) {
                                observer.observe(lastPost);
                            }
                        }
                    });
                }
            }
        });
    };

    observerInstance = new IntersectionObserver(callback, options);
    const lastPost = document.querySelector('.gh-postfeed .post:last-of-type, .gh-postfeed .gh-card:last-of-type');
    if (lastPost) {
        observerInstance.observe(lastPost);
    }
}
